import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  createMatter,
  getContacts,
  getContact,
  getPracticeAreas,
  getUsers,
  previewFolderName,
  type Contact,
  type UserSummary,
} from '../api/client';
import { useNavigate } from 'react-router-dom';
import { useIsMobile } from '../hooks/useIsMobile';
import { useToast } from './ToastProvider';
import Dialog from './Dialog';

const inputStyle = {
  width: '100%',
  padding: '0.4rem 0.6rem',
  borderRadius: 6,
  border: '1px solid var(--border)',
  fontSize: '0.85rem',
  boxSizing: 'border-box' as const,
};

const labelStyle = {
  display: 'block',
  fontSize: '0.75rem',
  color: 'var(--text-secondary)',
  marginBottom: '0.2rem',
  fontWeight: 600 as const,
};

interface Props {
  onClose: () => void;
  defaultContactId?: number;
}

export default function CreateMatterModal({ onClose, defaultContactId }: Props) {
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { success } = useToast();

  const { data: practiceAreas } = useQuery({ queryKey: ['practice-areas'], queryFn: getPracticeAreas });
  const { data: users = [] } = useQuery<UserSummary[]>({ queryKey: ['users'], queryFn: () => getUsers() });

  // ── Contact search state ─────────────────────────────────
  const [contactSearch, setContactSearch] = useState('');
  const [selectedContact, setSelectedContact] = useState<Contact | null>(null);
  const [showContactDropdown, setShowContactDropdown] = useState(false);
  const contactRef = useRef<HTMLDivElement>(null);

  // Load default contact by ID when provided
  const { data: defaultContact } = useQuery({
    queryKey: ['contact', defaultContactId],
    queryFn: () => getContact(defaultContactId!),
    enabled: !!defaultContactId,
  });

  // Adjusted while rendering, not in an effect: an effect would paint the
  // modal once with no contact before selecting it.
  if (defaultContact && !selectedContact) {
    setSelectedContact(defaultContact);
    setContactSearch(`${defaultContact.first_name} ${defaultContact.last_name}`);
  }

  // Search contacts via API as user types
  const { data: contactSearchData } = useQuery({
    queryKey: ['contact-search', contactSearch],
    queryFn: () => getContacts({ search: contactSearch.trim(), limit: 20 }),
    enabled: contactSearch.trim().length >= 2 && showContactDropdown && !selectedContact,
  });
  const contactResults = contactSearchData?.items ?? [];

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (contactRef.current && !contactRef.current.contains(e.target as Node)) {
        setShowContactDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSelectContact = (c: Contact) => {
    setSelectedContact(c);
    setContactSearch(`${c.first_name} ${c.last_name}`);
    setShowContactDropdown(false);
  };

  const handleClearContact = () => {
    setSelectedContact(null);
    setContactSearch('');
    setShowContactDropdown(false);
  };

  const [form, setForm] = useState({
    title: '',
    practice_area_id: '',
    intake_paralegal_id: '',
    paralegal_id: '',
    attorney_id: '',
    court_key: '',
    description: '',
  });

  // Court is no longer auto-defaulted from contact state — staff sets it
  // manually so removed/remanded cases don't inherit the wrong venue (#436).

  // ── Folder name preview ────────────────────────────────
  const [folderPreview, setFolderPreview] = useState<{
    original: string; abbreviated: string; length: number; max_chars: number; needs_abbreviation: boolean;
  } | null>(null);
  const [customFolderName, setCustomFolderName] = useState('');
  const [folderPromptShown, setFolderPromptShown] = useState(false);
  const previewTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  // With no title or client there is nothing to preview. The old preview is
  // dropped while rendering (not in the effect below) the moment either
  // empties, so it cannot reappear before a fresh one arrives.
  const folderInputsReady = Boolean(form.title.trim() && selectedContact);
  const [folderInputsWereReady, setFolderInputsWereReady] = useState(folderInputsReady);
  if (folderInputsReady !== folderInputsWereReady) {
    setFolderInputsWereReady(folderInputsReady);
    if (!folderInputsReady) {
      setFolderPreview(null);
      setFolderPromptShown(false);
    }
  }

  useEffect(() => {
    const title = form.title.trim();
    if (!title || !selectedContact) return;
    // Cleared below once the title or client changes, so a reply for the old
    // ones (already in flight) cannot bring back their preview.
    let current = true;
    previewTimeout.current = setTimeout(async () => {
      try {
        const preview = await previewFolderName({
          title,
          client_first: selectedContact.first_name,
          client_last: selectedContact.last_name,
        });
        if (!current) return;
        setFolderPreview(preview);
        if (preview.needs_abbreviation) {
          setCustomFolderName(preview.abbreviated);
          setFolderPromptShown(true);
        } else {
          setFolderPromptShown(false);
        }
      } catch {
        // Non-critical — just don't show the preview
      }
    }, 500);
    return () => {
      current = false;
      if (previewTimeout.current) clearTimeout(previewTimeout.current);
    };
  }, [form.title, selectedContact]);

  const mutation = useMutation({
    mutationFn: () =>
      createMatter({
        title: form.title.trim(),
        contact_id: selectedContact!.id,
        practice_area_id: form.practice_area_id ? Number(form.practice_area_id) : null,
        intake_paralegal_id: form.intake_paralegal_id ? Number(form.intake_paralegal_id) : null,
        paralegal_id: form.paralegal_id ? Number(form.paralegal_id) : null,
        attorney_id: form.attorney_id ? Number(form.attorney_id) : null,
        description: form.description.trim() || null,
        court_key: form.court_key || null,
        ...(folderPromptShown && customFolderName.trim() ? { folder_title_override: customFolderName.trim() } : {}),
      } as Record<string, unknown>),
    onSuccess: (matter) => {
      queryClient.invalidateQueries({ queryKey: ['matters'] });
      success('Matter created');
      onClose();
      navigate(`/matters/${matter.id}`);
    },
  });

  const [attempted, setAttempted] = useState(false);
  const canSubmit = form.title.trim() && selectedContact && form.court_key;

  const set = (field: string) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) =>
    setForm((f) => ({ ...f, [field]: e.target.value }));

  return (
    <Dialog
      onClose={onClose}
      ariaLabelledBy="create-matter-title"
      contentStyle={{
          background: 'var(--card)',
          borderRadius: 'var(--radius-md)',
          padding: '1.5rem',
          width: 'min(540px, 92vw)',
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
    >
        <h2 id="create-matter-title" style={{ margin: '0 0 1rem', fontSize: '1.1rem' }}>New Matter</h2>

        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '0.75rem' }}>
          {/* Title — full width */}
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Title *</label>
            <input
              style={{ ...inputStyle, ...(attempted && !form.title.trim() ? { borderColor: '#ef4444', boxShadow: '0 0 0 1px #ef4444' } : {}) }}
              value={form.title}
              onChange={set('title')}
              autoFocus
              placeholder="e.g. Smith Ch. 7 Bankruptcy"
            />
            {attempted && !form.title.trim() && <span style={{ color: '#ef4444', fontSize: '0.72rem' }}>Required</span>}
          </div>

          {/* Folder name preview when title is too long */}
          {folderPromptShown && folderPreview && (
            <div style={{
              gridColumn: '1 / -1',
              background: 'var(--warning-light, #fff8e1)',
              border: '1px solid var(--warning, #f0ad4e)',
              borderRadius: 6,
              padding: '0.6rem 0.75rem',
              fontSize: '0.82rem',
            }}>
              <div style={{ fontWeight: 600, marginBottom: '0.3rem', color: 'var(--warning-dark, #8a6d3b)' }}>
                Folder name too long ({folderPreview.original.length} chars, max {folderPreview.max_chars})
              </div>
              <label style={{ ...labelStyle, marginBottom: '0.25rem' }}>Dropbox folder name:</label>
              <input
                style={{ ...inputStyle, fontFamily: 'monospace', fontSize: '0.8rem' }}
                value={customFolderName}
                onChange={(e) => setCustomFolderName(e.target.value)}
              />
              <div style={{ color: 'var(--text-muted)', marginTop: '0.2rem', fontSize: '0.75rem' }}>
                {customFolderName.length} / {folderPreview.max_chars} chars
              </div>
            </div>
          )}

          {/* Contact — searchable type-ahead */}
          <div style={{ gridColumn: '1 / -1', position: 'relative' }} ref={contactRef}>
            <label style={labelStyle}>Contact *</label>
            {attempted && !selectedContact && <span style={{ color: '#ef4444', fontSize: '0.72rem', marginBottom: '0.15rem', display: 'block' }}>Please select a contact</span>}
            {selectedContact ? (
              <div style={{
                ...inputStyle,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                background: 'var(--primary-light)',
                borderColor: 'var(--primary)',
              }}>
                <span style={{ fontWeight: 600, fontSize: '0.85rem' }}>
                  {selectedContact.first_name} {selectedContact.last_name}
                  {selectedContact.email ? <span style={{ fontWeight: 400, color: 'var(--text-muted)', marginLeft: '0.4rem' }}>({selectedContact.email})</span> : ''}
                </span>
                <button
                  type="button"
                  onClick={handleClearContact}
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '1rem',
                    color: 'var(--text-muted)',
                    padding: '0 0.2rem',
                    lineHeight: 1,
                  }}
                  title="Change contact"
                >
                  ×
                </button>
              </div>
            ) : (
              <input
                style={inputStyle}
                value={contactSearch}
                onChange={(e) => {
                  setContactSearch(e.target.value);
                  setShowContactDropdown(true);
                }}
                onFocus={() => contactSearch.trim().length >= 2 && setShowContactDropdown(true)}
                placeholder="Type to search contacts..."
              />
            )}
            {showContactDropdown && !selectedContact && contactSearch.trim().length >= 2 && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: 0,
                right: 0,
                background: 'var(--card)',
                border: '1px solid var(--border)',
                borderRadius: '0 0 6px 6px',
                maxHeight: 200,
                overflowY: 'auto',
                zIndex: 1010,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              }}>
                {contactResults.length === 0 ? (
                  <div style={{ padding: '0.5rem 0.75rem', color: 'var(--text-muted)', fontSize: '0.82rem' }}>
                    No contacts found
                  </div>
                ) : (
                  contactResults.map((c: Contact) => (
                    <div
                      key={c.id}
                      onClick={() => handleSelectContact(c)}
                      onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--primary-light)')}
                      onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
                      style={{
                        padding: '0.45rem 0.75rem',
                        cursor: 'pointer',
                        fontSize: '0.85rem',
                        borderBottom: '1px solid var(--border-light)',
                      }}
                    >
                      <span style={{ fontWeight: 600 }}>{c.first_name} {c.last_name}</span>
                      {c.email && <span style={{ color: 'var(--text-muted)', marginLeft: '0.4rem' }}>({c.email})</span>}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Practice Area */}
          <div>
            <label style={labelStyle}>Practice Area</label>
            <select style={inputStyle} value={form.practice_area_id} onChange={set('practice_area_id')}>
              <option value="">None</option>
              {(practiceAreas ?? []).map((pa) => (
                <option key={pa.id} value={pa.id}>{pa.name}</option>
              ))}
            </select>
          </div>

          {/* Jurisdiction */}
          <div>
            <label style={labelStyle}>Jurisdiction <span style={{ color: '#dc2626' }}>*</span></label>
            <select
              style={{ ...inputStyle, borderColor: attempted && !form.court_key ? '#dc2626' : undefined }}
              value={form.court_key}
              onChange={set('court_key')}
            >
              <option value="">Select jurisdiction...</option>
              <option value="wv_state">West Virginia (State)</option>
              <option value="nc_state">North Carolina (State)</option>
              <option value="wa_state">Washington (State)</option>
              <option value="ndwv_federal">WV Northern District (Federal)</option>
              <option value="sdwv_federal">WV Southern District (Federal)</option>
              <option value="wdnc_federal">NC Western District (Federal)</option>
              <option value="mdnc_federal">NC Middle District (Federal)</option>
              <option value="ednc_federal">NC Eastern District (Federal)</option>
              <option value="wawd_federal">WA Western District (Federal)</option>
              <option value="waed_federal">WA Eastern District (Federal)</option>
              <option value="tned_federal">TN Eastern District (Federal)</option>
              <option value="azd_federal">District of Arizona (Federal)</option>
            </select>
            {attempted && !form.court_key && <div style={{ color: '#dc2626', fontSize: '0.72rem', marginTop: '0.15rem' }}>Jurisdiction is required</div>}
          </div>

          {/* Staff assignments */}
          <div>
            <label style={labelStyle}>Intake Paralegal</label>
            <select style={inputStyle} value={form.intake_paralegal_id} onChange={set('intake_paralegal_id')}>
              <option value="">None</option>
              {(users ?? []).map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Paralegal</label>
            <select style={inputStyle} value={form.paralegal_id} onChange={set('paralegal_id')}>
              <option value="">None</option>
              {(users ?? []).map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Attorney</label>
            <select style={inputStyle} value={form.attorney_id} onChange={set('attorney_id')}>
              <option value="">None</option>
              {(users ?? []).filter((u) => u.role === 'attorney' || u.role === 'admin').map((u) => (
                <option key={u.id} value={u.id}>{u.name}</option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={labelStyle}>Description</label>
            <textarea
              style={{ ...inputStyle, resize: 'vertical' }}
              rows={3}
              value={form.description}
              onChange={set('description')}
            />
          </div>
        </div>

        {mutation.isError && (
          <p style={{ color: '#c0392b', fontSize: '0.85rem', margin: '0.75rem 0 0' }}>
            {(mutation.error as { response?: { data?: { detail?: string } } })?.response?.data?.detail
              || 'Error creating matter. Please try again.'}
          </p>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.25rem' }}>
          <button
            onClick={onClose}
            style={{
              padding: '0.5rem 1rem',
              background: 'var(--bg)',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: '0.85rem',
            }}
          >
            Cancel
          </button>
          <button
            onClick={() => {
              setAttempted(true);
              if (canSubmit) mutation.mutate();
            }}
            disabled={mutation.isPending}
            style={{
              padding: '0.5rem 1rem',
              background: 'var(--primary)',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              cursor: 'pointer',
              fontSize: '0.85rem',
              opacity: (!canSubmit || mutation.isPending) ? 0.5 : 1,
            }}
          >
            {mutation.isPending ? 'Creating...' : 'Create Matter'}
          </button>
        </div>
    </Dialog>
  );
}
